<template>
  <div id="activatedTable" class="data-card card" data-step="10">
    <div class="data-container">
      <div class="title">Liste des talents activés pour cette campagne</div>
      <p>Ces talents peuvent désormais compléter leur évaluation en ligne.</p>
      <Table
        :data="participantStore.inProgressParticipants"
        :fields="state.fields"
        :search="search"
        :bulk="
          !campaignStore.currentCampaign.archived
            ? {
                actions: [
                  {
                    icon: 'bi-pause-circle-fill',
                    label: 'Désactiver les évaluations',
                    class: 'primary',
                    action: (participants) => {
                      openActionModal([...participants], 'desactivate');
                    },
                    display: (participants) => {
                      return participants.every((elt) => elt.state === 0);
                    },
                  },
                  {
                    icon: 'bi-arrow-left-right',
                    label: 'Déplacer les talents',
                    class: 'secondary outline',
                    action: (participants) => {
                      openActionModal([...participants], 'move');
                    },
                    display: (participants) => {
                      return participants.every((elt) => elt.state === 0);
                    },
                  },
                  {
                    class: 'red',
                    icon: 'bi-trash',
                    label: 'Supprimer les talents',
                    action: (participants) => {
                      openActionModal([...participants], 'delete');
                    },
                    display: (participants) => {
                      return participants.every((elt) => elt.state === 0);
                    },
                  },
                ],
              }
            : null
        "
        @clickOnRow="openParticipantModal"
      >
        <template #[`candidate.last_name`]="{ data }">
          <span v-if="data.candidate_id"
            >{{ data.candidate.first_name }} {{ data.candidate.last_name }}
          </span>
          <span v-else>
            <p
              v-if="!data.pending_first_name || !data.pending_last_name"
              class="muted"
            >
              Non renseigné
            </p>
            <template v-else>
              {{ data.pending_first_name }} {{ data.pending_last_name }}
            </template>
          </span>
        </template>
        <template #[`candidate.email`]="{ data }">
          <span v-if="data.candidate_id" v-text="data.candidate.email" />
          <span v-else v-text="data.pending_email" />
        </template>
        <template #creation_time="{ data }">
          <div
            :style="{ width: 'fit-content' }"
            v-text="getDate(data.creation_time, 'D MMM YYYY')"
            v-tooltip="{
              content: getDate(data.creation_time, 'D MMM YYYY à HH[h]mm'),
              placement: 'top',
            }"
          />
        </template>
        <template #progress="{ data }">
          <div class="container">
            <div
              v-if="data.state === 0 && !data.candidate"
              class="tag grey-light"
              v-text="'Compte non créé'"
              v-tooltip="'Ce talent n’a pas encore créé son compte Yuzu'"
            />
            <div
              v-else-if="data.state === 0 && data.candidate"
              class="tag green"
              v-text="'En attente de démarrage'"
              v-tooltip="
                'Ce talent a créé son compte et peut démarrer son évaluation'
              "
            />
            <div
              v-else-if="data.state === 1"
              class="progress-bar-container"
              v-tooltip="
                'Ce talent a commencé son évaluation, vous pourrez suivre ici sa progression'
              "
            >
              <span>{{ data.progress || 0 }}%</span>
              <div class="bar">
                <div
                  class="progress"
                  :style="{ width: `${data.progress || 0}%` }"
                />
              </div>
            </div>
            <div
              v-else-if="data.state !== 2"
              class="tag orange"
              v-text="'Calcul des résultats'"
              v-tooltip="
                'Les résultats de ce talent sont en cours de calculs et seront bientôt disponibles'
              "
            />
            <div
              v-else-if="data.state === 2"
              class="tag red"
              v-text="'Erreur (V1-RESULT-DUPLICATE)'"
              v-tooltip="
                'Une erreur est survenue, merci de contacter le support en nous fournisant le code d\'erreur suivant: V1-RESULT-DUPLICATE'
              "
            />
          </div>
        </template>
        <template #actions="actionsProps">
          <Button
            v-if="actionsProps.row.state === 0"
            label="Désactiver l'évaluation"
            class="icon primary"
            icon="bi-pause-circle-fill"
            data-step="11"
            @click="openActionModal([actionsProps.row], 'desactivate')"
            :disabled="campaignStore.currentCampaign.archived"
          />
          <Button
            v-else
            label="Assistance"
            class="icon outline green"
            icon="bi-question-circle"
            data-step="11"
            @click="state.showHelpModal = true"
            :disabled="campaignStore.currentCampaign.archived"
          />
          <div @click.stop="null">
            <VDropdown
              :distance="10"
              :disabled="campaignStore.currentCampaign.archived"
              placement="left"
            >
              <div
                class="btn outline secondary icon square"
                :class="{ disabled: campaignStore.currentCampaign.archived }"
              >
                <i class="bi bi-three-dots-vertical" />
              </div>
              <template #popper>
                <div
                  v-if="actionsProps.row.state !== 1"
                  class="action red-color"
                  v-close-popper
                  @click="openActionModal([actionsProps.row], 'delete')"
                >
                  <i class="bi bi-trash" />
                  <span>Supprimer le talent</span>
                </div>
                <div v-if="actionsProps.row.state !== 1" class="separator" />
                <div
                  v-if="actionsProps.row.state !== 2"
                  class="action"
                  v-tooltip="{
                    content: getLastReminder(actionsProps.row),
                  }"
                  v-close-popper
                  @click="sendReminderToParticipant(actionsProps.row)"
                >
                  <i class="bi bi-arrow-counterclockwise" />
                  <span>Relancer le talent</span>
                </div>
                <div
                  v-if="actionsProps.row.state !== 1"
                  class="action"
                  v-close-popper
                  @click="openActionModal([actionsProps.row], 'move')"
                >
                  <i class="bi bi-arrow-left-right" />
                  <span>Déplacer le talent</span>
                </div>
              </template>
            </VDropdown>
          </div>
        </template>
        <template #empty>
          <Empty
            message="Aucune évaluation en cours pour le moment"
            :action="{
              label: 'Voir les talents activables',
              click: () => {
                appStore.currentCampaignTab = 'ToActive';
              },
            }"
            :dark="isDark"
            icon="box"
          />
        </template>
      </Table>
      <ParticipantModal
        :isOpen="state.showParticipantModal"
        @close="state.showParticipantModal = !state.showParticipantModal"
      />
      <ActionModal
        :modal="state.actionModal.modal"
        :data="state.participants"
        :isOpen="state.actionModal.show"
        @close="state.actionModal.show = false"
        @confirm="state.actionModal.show = false"
      />
      <HelpModal
        :isOpen="state.showHelpModal"
        @close="state.showHelpModal = !state.showHelpModal"
      />
    </div>
  </div>
</template>

<script setup>
import { reactive, inject } from "vue";
import { storeToRefs } from "pinia";
import { useCampaignStore } from "@/stores/campaign.js";
import { useParticipantStore } from "@/stores/participant.js";
import { useAppStore } from "@/stores/app.js";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { useDark } from "@vueuse/core";
import { getDate, dateIsBefore } from "@/composables/utils";
import Empty from "@/components/table/Empty.vue";
import ParticipantModal from "@/components/modals/app/participantModal/ParticipantModal.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import HelpModal from "@/components/modals/app/helpModal/HelpModal.vue";

const isDark = useDark();

const route = useRoute();

const appStore = useAppStore();

const moment = inject("moment");

const campaignStore =
  route.name === "campaign" ? useCampaignStore() : useOnboardingStore();

const participantStore =
  route.name === "campaign" ? useParticipantStore() : useOnboardingStore();

const { currentParticipant } = storeToRefs(participantStore);

const toast = useToast();

defineProps({
  search: {
    type: Object,
  },
});

const state = reactive({
  showParticipantModal: false,
  actionModal: {
    show: false,
    modal: "",
  },
  showHelpModal: false,
  participants: [],
  fields: [
    {
      id: "candidate.last_name",
      fallback: "pending_last_name",
      label: "Talent",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "candidate.email",
      fallback: "pending_email",
      label: "Adresse e-mail",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "creation_time",
      label: "Date d'ajout",
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
    {
      id: "progress",
      label: "Avancement de l'évaluation",
      multipleSort: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          if (b.candidate && !a.candidate) return -1;
          if (!b.candidate && a.candidate) return 1;

          if (b.state > 0 && a.state === 0) return -1;
          if (b.state === 0 && a.state > 0) return 1;

          if (a.progress !== b.progress) return a.progress - b.progress;

          return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
        } else {
          if (a.candidate && !b.candidate) return -1;
          if (!a.candidate && b.candidate) return 1;

          if (a.state > 0 && b.state === 0) return -1;
          if (a.state === 0 && b.state > 0) return 1;

          if (a.progress !== b.progress) return b.progress - a.progress;

          return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
        }
      },
    },
  ],
});

function openParticipantModal(participant) {
  state.showParticipantModal = true;
  currentParticipant.value = participant;
}

function openActionModal(participants, modal) {
  state.participants = participants;
  state.actionModal = {
    show: true,
    modal,
  };
}

async function sendReminderToParticipant(participant) {
  const diff =
    moment().diff(
      moment.utc(participant.last_reminder_time, "YYYY/MM/DD HH:mm:ss")
    ) / 1000;

  if (!isNaN(diff) && diff < 1800) {
    //if already sent a reminder in the last 30 minutes, don't event send an API call
    toast.info("Une relance a déjà été envoyée récemment !");

    return;
  }

  const reminder = await participantStore.sendReminder(
    participant.participant_id,
    route.params.companyId,
    route.params.spaceId,
    route.params.campaignId
  );

  if (reminder.key) {
    toast.error("Erreur lors de l'envoi de la relance. Reessayez plus tard");
  } else if (reminder.sent) {
    toast.success("La relance a bien été envoyée !");
  } else {
    toast.info("Une relance a déjà été envoyée récemment !");
  }
}

function getLastReminder(participant) {
  if (participant.state === 2)
    return "Ce participant a terminé son assessment, vous ne pouvez donc pas lui envoyer de relance";

  if (participant.last_reminder_time === null) {
    return "Aucune relance pour le moment";
  }
  return (
    "Dernière relance : " +
    getDate(participant.last_reminder_time, "D MMM YYYY à HH[h]mm")
  );
}
</script>
